import { type FC, useEffect, useState, useMemo, useRef, useCallback } from "react";
import {
  ArrowRight,
  Check,
  ChevronRight,
  Clock,
  DollarSign,
  FileText,
  Gavel,
  Home,
  Landmark,
  Loader2,
  Lock,
  Users,
  X,
} from "lucide-react";

import MainLayout from "@/pages/layouts/MainLayout";
import { PageActions } from "@/pages/layouts/components/PageActions";
import { Tag } from "@/shared/components/ui/tag";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "@/shared/components/ui/sheet";
import { MultipleChoice } from "@/shared/components/FlowController/questions/MultipleChoice";
import PostLossPaywall from "@/pages/postloss/components/PostLossPaywall";
import { EstateExpertWidget } from "@/pages/postloss/overview/components/EstateExpertWidget";
import { ProbateResultsScreen } from "@/pages/postloss/probate/components/ProbateResultsScreen";
import { getSignoffLoopState } from "@/pages/postloss/probate/components/family-signoffs/SignoffLoopScreen";
import { buildScreensForTask } from "@/pages/postloss/probate/tasks";
import { useProbateTaskAnswers } from "@/shared/hooks/useProbateTaskAnswers";
import { useProbateStepSave } from "@/shared/hooks/useProbateStepSave";
import { useFlowSection } from "@/shared/hooks/useFlowSection";
import { useSaveCalendlyBooking } from "@/shared/hooks/useSaveCalendlyBooking";
import { CalendlyModal } from "@/shared/components/overlay/CalendlyModal";
import { CALENDLY_URLS } from "@/shared/constants/calendlyUrls";
import { POST_LOSS_ROUTES } from "@/shared/constants/paths";
import { TASK_IDS } from "@/shared/constants/taskIds";
import { trackEvent, EventType } from "@/shared/utils/core";
import { parseConsentEntries } from "@/shared/utils/consentUtils";
import { enqueueSnackbar } from "notistack";
import { TOAST } from "@/shared/constants/toastMessages";

import type { PaymentTier } from "@/shared/utils/tierOrder";
import type { FlowSection } from "@/shared/constants/flowTypes";
import type { HeirConsentOption, SignOffStatus } from "@/shared/types/probateTypes";

import { AuthorityResultsCard } from "./AuthorityResultsCard";
import { KeyDetailsCard } from "./KeyDetailsCard";
import { InfoRow } from "./InfoRow";
import { KnowRow } from "./KnowRow";

import paywallBlurredBg from "@/shared/assets/get-authority/paywall-content-blurred.png";

interface RoadmapStep {
  number: number;
  title: string;
  description?: string;
  taskId?: string;
}

const PROBATE_STEPS: RoadmapStep[] = [
  {
    number: 1,
    title: "Check the will",
    description:
      "The court examines the physical document, not just what it says. A few questions now prevent the most common reasons filings get sent back.",
    taskId: TASK_IDS.PROBATE_CHECK_WILL,
  },
  {
    number: 2,
    title: "Confirm executor and witnesses",
    description:
      "Verify who is named to handle the estate and who witnessed the signing.",
    taskId: TASK_IDS.PROBATE_EXECUTOR_WITNESSES,
  },
  {
    number: 3,
    title: "Review family members",
    description:
      "Confirm closest family, beneficiaries, and organizations named in the will.",
    taskId: TASK_IDS.PROBATE_FAMILY_TRIAGE,
  },
  {
    number: 4,
    title: "Family sign-offs",
    description:
      "Record each heir's consent status. Those who agree will sign a waiver; others will require a citation or affidavit.",
    taskId: TASK_IDS.PROBATE_FAMILY_SIGNOFFS,
  },
];

const CONTENT = {
  heading: (name: string) => `${name}'s estate goes through probate`,
  description: (name: string) =>
    `Being named in a will does not grant legal power yet. The court must review the will and issue Letters Testamentary so you can manage ${name}'s affairs.`,
  timeline: "Typically 2 to 6 months to get official authority",
  filingFee: "$215 to $625, set by estate size",
  attorneyStatus: "Optional in NY: File yourself or request a referral",
  paywallTitle: "Unlock your probate roadmap",
  paywallChecklist: [
    { text: "Know exactly what to gather, before the court asks" },
    { text: "Plain-language guidance for every form, waiver, and fee" },
    { text: "Your details collected once and used everywhere they're needed" },
    { text: "The attorney hotline and an Estate Expert session", helpline: true },
  ],
};

const FAMILY_TRIAGE_SCREEN_TITLES: Record<string, string> = {
  family_triage_intro: "Review family members",
  family_triage_marriages: "Marriages",
  family_triage_children: "Children",
  family_triage_extended_family: "Family",
  family_triage_review: "Review",
  family_triage_loading: "Building list",
  family_triage_reveal: "Your Sign-Off Plan",
};

const SIGNOFF_SCREEN_TITLES: Record<string, string> = {
  family_signoff_loop: "Family sign-offs",
};

const CONSENT_TO_SIGN_OFF_STATUS: Record<HeirConsentOption, SignOffStatus> = {
  willing_to_sign: "received",
  uncooperative: "citation_needed",
  minor_or_incapacitated: "guardian_needed",
  cannot_locate: "not_located",
  deceased_after: "family_update_needed",
};

interface ProbateRoadmapProps {
  estateId: number;
  decedentFirstName: string;
  county: string | null;
  countyLabel: string;
  isPaid: boolean;
  tier: PaymentTier | null;
  helplineEnabled: boolean;
  isFirstVisit: boolean;
  onDismissFirstVisit: () => void;
  familyCount: number;
  propertyCount: number;
  keyDocCount: number;
  keyDocTotal: number;
  attorneyName: string | null;
}

export const ProbateRoadmap: FC<ProbateRoadmapProps> = ({
  estateId,
  decedentFirstName,
  county,
  countyLabel,
  isPaid,
  tier,
  helplineEnabled,
  isFirstVisit,
  onDismissFirstVisit,
  familyCount,
  propertyCount,
  keyDocCount,
  keyDocTotal,
  attorneyName,
}) => {
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [calendlyOpen, setCalendlyOpen] = useState(false);
  const [activeFlow, setActiveFlow] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [preparingForms, setPreparingForms] = useState(false);

  const { taskRows, initialAnswers } = useProbateTaskAnswers(estateId);
  const { saveStep, completeTask, resetPendingSave } = useProbateStepSave();
  const { mutate: saveCalendlyBooking } = useSaveCalendlyBooking(estateId, "get_authorized");

  const handleEventScheduled = useCallback(
    (eventUri: string, inviteeUri: string) => {
      saveCalendlyBooking({ eventUri, inviteeUri });
    },
    [saveCalendlyBooking],
  );

  const preparingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sheetContentRef = useRef<HTMLDivElement>(null);
  const activeFlowRef = useRef(activeFlow);
  activeFlowRef.current = activeFlow;
  const flowAnswersRef = useRef<Record<string, string | string[]>>({});

  const completedTaskIds = useMemo(() => {
    const set = new Set<string>();
    for (const row of taskRows ?? []) {
      if (row.status === "completed") set.add(row.task_id);
    }
    return set;
  }, [taskRows]);

  const startedTaskIds = useMemo(() => {
    const set = new Set<string>();
    for (const row of taskRows ?? []) {
      if (row.status === "in_progress") set.add(row.task_id);
    }
    return set;
  }, [taskRows]);

  const allStepsCompleted = useMemo(
    () => PROBATE_STEPS.every((step) => !step.taskId || completedTaskIds.has(step.taskId)),
    [completedTaskIds],
  );

  const activeScreens = useMemo(
    () => (activeFlow ? buildScreensForTask(activeFlow) : []),
    [activeFlow],
  );

  const handleFlowComplete = useCallback(async () => {
    if (isSaving) return;
    setIsSaving(true);
    const taskId = activeFlowRef.current;
    try {
      if (taskId) {
        if (taskId === TASK_IDS.PROBATE_FAMILY_SIGNOFFS) {
          const consents = parseConsentEntries(flowAnswersRef.current["family_signoff_consents"]);
          const contactUpdates = Object.entries(consents)
            .map(([contactId, entry]) => ({
              id: Number(contactId),
              sign_off_status: CONSENT_TO_SIGN_OFF_STATUS[entry.status],
            }))
            .filter((c) => !isNaN(c.id) && c.id > 0);

          await completeTask(taskId, flowAnswersRef.current, contactUpdates.length > 0 ? contactUpdates : undefined);
          trackEvent(EventType.PROBATE_STEP_COMPLETED, { estate_id: estateId, step_id: taskId });
          setPreparingForms(true);
          preparingTimerRef.current = setTimeout(() => setPreparingForms(false), 3000);
        } else {
          await completeTask(taskId, flowAnswersRef.current);

          if (taskId === TASK_IDS.PROBATE_FAMILY_TRIAGE) {
            trackEvent(EventType.FAMILY_TRIAGE_COMPLETED, { estate_id: estateId });
            enqueueSnackbar("Family confirmed. Your contacts and roadmap are updated.", { variant: "success" });
          } else {
            trackEvent(EventType.PROBATE_STEP_COMPLETED, { estate_id: estateId, step_id: taskId });
          }
        }
      }
      setActiveFlow(null);
    } catch (err) {
      console.error("Failed to complete task:", err);
      enqueueSnackbar(TOAST.GENERIC_ERROR, { variant: "error" });
    } finally {
      setIsSaving(false);
      resetPendingSave();
    }
  }, [completeTask, resetPendingSave, estateId, isSaving]);

  const initialStep = useMemo(() => {
    if (!activeScreens.length || !Object.keys(initialAnswers).length) return 0;

    const taskId = activeFlow;
    const taskRow = taskRows?.find((r) => r.task_id === taskId);
    const isCompleted = taskRow?.status === "completed";

    const visibleScreens = activeScreens.filter((s) => {
      if (!s.shouldDisplay) return true;
      return s.shouldDisplay({ answers: initialAnswers, currentStep: 0 });
    });

    if (isCompleted) return Math.max(visibleScreens.length - 1, 0);

    let lastAnswered = 0;
    for (let i = 0; i < visibleScreens.length; i++) {
      const screen = visibleScreens[i];
      if (initialAnswers[screen.id] === "completed" || (screen.question && initialAnswers[screen.question.id])) {
        lastAnswered = i + 1;
      }
    }
    return Math.min(lastAnswered, visibleScreens.length - 1);
  }, [activeScreens, initialAnswers, activeFlow, taskRows]);

  const flowSection = useMemo((): FlowSection => ({
    id: activeFlow ?? "probate",
    title: "Probate",
    screens: activeScreens,
    allowSaveAndClose: true,
    saveOnEachStep: true,
    onComplete: handleFlowComplete,
    onStepComplete: saveStep,
  }), [activeFlow, activeScreens, saveStep, handleFlowComplete]);

  const {
    currentStep: flowStep,
    totalSteps: flowTotal,
    currentScreen,
    answers: flowAnswers,
    errors: flowErrors,
    setAnswer: setFlowAnswer,
    goNext: flowGoNext,
    goBack: flowGoBack,
    goToScreen: flowGoToScreen,
    isLastStep: flowIsLast,
  } = useFlowSection({ section: flowSection, initialStep, initialAnswers });

  flowAnswersRef.current = flowAnswers;

  const handleCloseSheet = useCallback(() => {
    const taskId = activeFlowRef.current;
    if (taskId && currentScreen) {
      saveStep(currentScreen.id, undefined, undefined, flowAnswersRef.current).catch(() => {
        enqueueSnackbar("Your latest answers may not have saved.", { variant: "warning" });
      });
    }
    setActiveFlow(null);
  }, [currentScreen, saveStep]);

  useEffect(() => {
    return () => {
      if (preparingTimerRef.current) clearTimeout(preparingTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (Object.keys(flowErrors).length > 0 && sheetContentRef.current) {
      const firstError = sheetContentRef.current.querySelector("[data-field-error]");
      if (firstError) {
        firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [flowErrors]);

  const activeStepConfig = PROBATE_STEPS.find((s) => s.taskId === activeFlow);

  const isNextDisabled = useMemo(() => {
    if (!currentScreen) return false;
    if (currentScreen.isButtonDisabled) {
      return currentScreen.isButtonDisabled({ answers: flowAnswers, currentStep: flowStep });
    }
    return false;
  }, [currentScreen, flowAnswers, flowStep]);

  const signoffLoopState = useMemo(() => {
    if (currentScreen?.id !== "family_signoff_loop") return null;
    return getSignoffLoopState(flowAnswers);
  }, [currentScreen?.id, flowAnswers]);

  return (
    <MainLayout header="Get authorized" actions={<PageActions />}>
      {preparingForms ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 min-h-[60vh]">
          <Loader2 className="size-12 animate-spin text-brand" />
          <p className="text-base font-semibold tracking-[-0.32px]">
            Preparing your court form packet...
          </p>
          <p className="text-sm text-secondary">
            Configuring forms for {countyLabel ?? "your county"} Surrogate's Court...
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-col lg:flex-row gap-8 py-8 items-start">
            <div className="flex-1 flex flex-col gap-6 min-w-0">
              {!isPaid && (
                <Card className="bg-white overflow-hidden">
                  <CardContent className="pt-6 flex flex-col gap-6">
                    <Tag color="green" icon={Check} className="w-fit">
                      Your results
                    </Tag>

                    <div className="flex flex-col gap-2">
                      <h2>{CONTENT.heading(decedentFirstName)}</h2>
                      <p className="text-sm text-secondary">
                        {CONTENT.description(decedentFirstName)}
                      </p>
                    </div>

                    <div className="flex flex-col gap-2">
                      <div className="border-t border-primary" />
                      <InfoRow
                        icon={Landmark}
                        label="Courthouse"
                        value={`Surrogate's Court${county ? `, ${county} County` : ""}`}
                      />
                      <div className="border-t border-primary" />
                      <InfoRow icon={Clock} label="Timeline" value={CONTENT.timeline} />
                      <div className="border-t border-primary" />
                      <InfoRow icon={DollarSign} label="Filing fee" value={CONTENT.filingFee} />
                      <div className="border-t border-primary" />
                      <InfoRow icon={Landmark} label="Attorney status" value={CONTENT.attorneyStatus} />
                    </div>
                  </CardContent>
                </Card>
              )}

              {isFirstVisit && !allStepsCompleted && (
                <Card className="bg-white">
                  <CardContent className="pt-6 flex flex-col gap-3">
                    <div className="flex items-start justify-between gap-4">
                      <h2>{CONTENT.heading(decedentFirstName)}</h2>
                      <Tag color="stone" icon={Check} className="shrink-0 px-[10px] py-[2px] text-[13px]">
                        Your results
                      </Tag>
                    </div>
                    <p className="text-sm text-secondary">
                      {CONTENT.description(decedentFirstName)}
                    </p>
                  </CardContent>
                </Card>
              )}

              {!allStepsCompleted && (
                <div className="flex flex-col gap-1">
                  <h3 className="font-semibold">Your roadmap to legal authority</h3>
                  <p className="text-sm text-secondary">
                    Tell us about the estate. We'll prepare the exact probate forms you need to file.
                  </p>
                </div>
              )}

              {isPaid ? (
                allStepsCompleted ? (
                  <ProbateResultsScreen
                    answers={initialAnswers}
                    onComplete={() => {}}
                    setAnswer={() => {}}
                    goToScreen={() => {}}
                    errors={{}}
                  />
                ) : (
                  <div className="flex flex-col">
                    {PROBATE_STEPS.map((step, idx) => {
                      const isCompleted = step.taskId ? completedTaskIds.has(step.taskId) : false;
                      const isStarted = step.taskId ? startedTaskIds.has(step.taskId) : false;
                      const isFirstIncomplete = !isCompleted && PROBATE_STEPS.every((prev) =>
                        prev.number < step.number
                          ? !prev.taskId || completedTaskIds.has(prev.taskId)
                          : true,
                      );
                      const isActive = isFirstIncomplete && !!step.taskId;
                      const isFirst = idx === 0;
                      const isLast = idx === PROBATE_STEPS.length - 1;
                      const roundTop = isFirst ? "rounded-t-lg" : "";
                      const roundBottom = isLast ? "rounded-b-lg" : "";

                      if (isActive) {
                        return (
                          <div
                            key={step.number}
                            className={`relative z-10 flex flex-col gap-3 bg-brand-subtle border border-brand p-4 -mt-px first:mt-0 ${roundTop} ${roundBottom}`}
                          >
                            <div className="flex items-start gap-4">
                              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand text-white text-sm font-medium leading-none">
                                {step.number}
                              </span>
                              <div className="flex flex-col gap-1">
                                <span className="text-sm font-semibold tracking-[-0.28px]">{step.title}</span>
                                {step.description && (
                                  <p className="text-[13px] text-secondary">{step.description}</p>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="brand"
                              className="ml-10 w-fit"
                              onClick={() => { onDismissFirstVisit(); setActiveFlow(step.taskId ?? null); }}
                            >
                              {isStarted ? "Continue" : "Get started"}
                              <ArrowRight className="h-4 w-4" />
                            </Button>
                          </div>
                        );
                      }

                      const handleStepClick = () => {
                        if (step.taskId && (isCompleted || isFirstIncomplete)) {
                          onDismissFirstVisit();
                          setActiveFlow(step.taskId);
                        }
                      };

                      return (
                        <div
                          key={step.number}
                          onClick={handleStepClick}
                          className={`flex items-center gap-4 bg-white border border-primary p-4 -mt-px ${roundTop} ${roundBottom} ${step.taskId && (isCompleted || isFirstIncomplete) ? "cursor-pointer hover:bg-subtle/50" : ""}`}
                        >
                          {isCompleted ? (
                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand">
                              <Check className="h-4 w-4 text-white" />
                            </span>
                          ) : (
                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary text-sm font-medium">
                              {step.number}
                            </span>
                          )}
                          <span className={`flex-1 text-sm font-medium tracking-[-0.28px] ${isCompleted ? "text-tertiary" : ""}`}>
                            {step.title}
                          </span>
                          {isCompleted && (
                            <ChevronRight className="h-4 w-4 text-secondary" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )
              ) : (
                <div className="relative rounded-lg">
                  <img
                    src={paywallBlurredBg}
                    alt=""
                    aria-hidden="true"
                    className="w-full pointer-events-none select-none"
                  />

                  <div className="absolute inset-0 z-10 flex justify-center pt-6 lg:pt-[50px] px-4 lg:px-0">
                    <div className="w-full max-w-[500px] rounded-lg border border-brand bg-white p-4 lg:p-6 flex flex-col gap-6 h-fit shadow-lg">
                      <div className="flex flex-col gap-3">
                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-subtle">
                          <Lock className="h-6 w-6 text-brand" />
                        </div>

                        <div className="flex flex-col gap-1">
                          <h2>{CONTENT.paywallTitle}</h2>
                          <p className="text-sm text-secondary">
                            Everything you need to file with {countyLabel} Surrogate's Court, built
                            from what you confirmed.
                          </p>
                        </div>

                        <div className="flex flex-col gap-2">
                          {CONTENT.paywallChecklist.filter((item) => helplineEnabled || !item.helpline).map((item) => (
                            <div key={item.text} className="flex items-center gap-2">
                              <Check className="h-5 w-5 shrink-0 text-brand" />
                              <span className="text-sm">{item.text}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="border-t border-primary" />

                      <div className="flex items-center justify-between">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1">
                            <span className="text-xl font-semibold">$500</span>
                            <span className="text-sm text-secondary">one time</span>
                          </div>
                          <span className="text-sm font-medium text-brand">
                            An eligible estate expense
                          </span>
                        </div>

                        <Button
                          variant="brand"
                          rounded
                          onClick={() => setPaywallOpen(true)}
                        >
                          Continue
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </div>

                      <p className="text-xs text-secondary text-center">
                        Pay from the estate, or pay now and reimburse yourself.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="w-full lg:w-[300px] shrink-0 flex flex-col gap-8">
              {isPaid ? (
                <>
                  {!isFirstVisit && (
                    <AuthorityResultsCard
                      heading={CONTENT.heading(decedentFirstName)}
                      description={CONTENT.description(decedentFirstName)}
                    />
                  )}

                  <KeyDetailsCard
                    county={county}
                    timeline={CONTENT.timeline}
                    filingFee={CONTENT.filingFee}
                    attorneyStatus={CONTENT.attorneyStatus}
                  />

                  <EstateExpertWidget
                    onSeePlans={() => setPaywallOpen(true)}
                  />
                </>
              ) : (
                <>
                  <EstateExpertWidget
                    onSeePlans={() => setPaywallOpen(true)}
                  />

                  <div className="flex flex-col gap-3">
                    <h3>What we know</h3>
                    <div className="rounded-lg border border-primary overflow-hidden shadow-sm">
                      <KnowRow
                        icon={Users}
                        label="Family map"
                        value={`${familyCount} ${familyCount === 1 ? "person" : "people"}`}
                      />
                      <KnowRow
                        icon={Home}
                        label="Property"
                        value={`${propertyCount} confirmed`}
                      />
                      <KnowRow
                        icon={Lock}
                        label="Will"
                        value="In the vault"
                      />
                      <KnowRow
                        icon={FileText}
                        label="Documents"
                        value={`${keyDocCount} of ${keyDocTotal} key docs`}
                      />
                      <KnowRow
                        icon={Gavel}
                        label="Attorney"
                        value={attorneyName ?? "Not added"}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <PostLossPaywall
            isOpen={paywallOpen}
            onOpenChange={setPaywallOpen}
            promptLocation="get_authorized"
            currentTier={tier}
            returnPath={POST_LOSS_ROUTES.GET_AUTHORIZED}
          />

          <Sheet open={!!activeFlow} onOpenChange={(open) => {
            if (!open) handleCloseSheet();
          }}>
            <SheetContent
              side="right"
              showClose={false}
              overlayClassName="bg-black/30 backdrop-blur-[2px]"
              className="w-full sm:max-w-none sm:w-[50vw] p-0 flex flex-col border-l border-primary shadow-[0_0_24px_0_rgba(0,0,0,0.09)] bg-white"
            >
              <div className="flex flex-col gap-3 border-b border-primary p-6 shrink-0">
                <div className="flex items-center gap-3 w-full">
                  {signoffLoopState ? (
                    <span className="text-base font-medium tracking-[-0.32px] text-secondary flex-1">
                      Heir {signoffLoopState.index + 1} of {signoffLoopState.total}
                    </span>
                  ) : (
                    <>
                      <span className="text-base font-medium tracking-[-0.02em] text-secondary shrink-0">
                        Step {flowStep + 1}
                      </span>
                      <span className="h-1 w-1 rounded-full bg-stone-400 shrink-0" aria-hidden="true" />
                      <span className="text-base font-medium tracking-[-0.02em] text-secondary flex-1 min-w-0 truncate">
                        {currentScreen?.id &&
                        (FAMILY_TRIAGE_SCREEN_TITLES[currentScreen.id] ??
                          SIGNOFF_SCREEN_TITLES[currentScreen.id])
                          ? (FAMILY_TRIAGE_SCREEN_TITLES[currentScreen.id] ??
                              SIGNOFF_SCREEN_TITLES[currentScreen.id])
                          : (activeStepConfig?.title ?? "")}
                      </span>
                    </>
                  )}
                  <button
                    onClick={handleCloseSheet}
                    className="flex h-5 w-5 shrink-0 items-center justify-center hover:opacity-70"
                    aria-label="Close"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="flex items-center gap-4 w-full">
                  <div className="relative flex-1 h-2 rounded-full bg-background overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 rounded-full bg-brand transition-all duration-300"
                      style={{
                        width: signoffLoopState && signoffLoopState.total > 0
                          ? `${((signoffLoopState.index + 1) / signoffLoopState.total) * 100}%`
                          : `${flowTotal > 0 ? ((flowStep + 1) / flowTotal) * 100 : 0}%`,
                      }}
                    />
                  </div>
                  <span className="text-sm text-secondary shrink-0 whitespace-nowrap">
                    {signoffLoopState
                      ? `${signoffLoopState.index + 1} of ${signoffLoopState.total}`
                      : `${flowStep + 1} of ${flowTotal}`}
                  </span>
                </div>
              </div>

              <SheetTitle className="sr-only">
                {activeStepConfig?.title ?? "Step"}
              </SheetTitle>

              <div ref={sheetContentRef} className="flex-1 min-h-0 overflow-y-auto bg-white p-8">
                {currentScreen && (
                  <div className="flex flex-col gap-6">
                    {currentScreen.customComponent ? (
                      <currentScreen.customComponent
                        onComplete={() => flowGoNext()}
                        answers={flowAnswers}
                        setAnswer={setFlowAnswer}
                        goToScreen={flowGoToScreen}
                        errors={flowErrors}
                      />
                    ) : (
                      <>
                        {(currentScreen.title || currentScreen.subtitle) && (
                          <div className="flex flex-col gap-1">
                            {currentScreen.title && (
                              <p className="font-sans text-xl font-semibold leading-[30px]">
                                {currentScreen.title}
                              </p>
                            )}
                            {currentScreen.subtitle && (
                              <p className="text-sm text-secondary tracking-[-0.28px]">
                                {currentScreen.subtitle}
                              </p>
                            )}
                          </div>
                        )}
                        {currentScreen.question && currentScreen.question.type === "multiple_choice" && (
                          <MultipleChoice
                            question={currentScreen.question}
                            value={flowAnswers[currentScreen.question.id] as string | undefined}
                            onChange={(val) => setFlowAnswer(currentScreen.question!.id, val)}
                            errorMessage={flowErrors[currentScreen.question.id]}
                          />
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>

              {signoffLoopState ? (
                <div className="flex items-center justify-between border-t border-primary p-6 shrink-0">
                  {signoffLoopState.index === 0 ? (
                    <Button variant="outline" onClick={() => flowGoBack()}>
                      <ArrowRight className="h-4 w-4 rotate-180" />
                      Back
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => setFlowAnswer("signoff_loop_index", String(Math.max(0, signoffLoopState.index - 1)))}
                    >
                      <ArrowRight className="h-4 w-4 rotate-180" />
                      Back
                    </Button>
                  )}
                  <Button
                    variant="brand"
                    disabled={isSaving}
                    onClick={() => {
                      if (!signoffLoopState.hasSelection) return;
                      if (signoffLoopState.index >= signoffLoopState.total - 1) {
                        flowGoNext();
                      } else {
                        setFlowAnswer("signoff_loop_index", String(signoffLoopState.index + 1));
                      }
                    }}
                  >
                    Next
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between border-t border-primary p-6 shrink-0">
                  {flowStep > 0 ? (
                    <Button variant="outline" onClick={() => flowGoBack()}>
                      Back
                    </Button>
                  ) : (
                    <div />
                  )}
                  {!(typeof currentScreen?.hideButton === "function"
                    ? currentScreen.hideButton({ answers: flowAnswers, currentStep: flowStep })
                    : currentScreen?.hideButton) && (
                    <Button
                      variant={isNextDisabled || isSaving ? "outline" : "brand"}
                      disabled={isNextDisabled || isSaving}
                      onClick={() => flowGoNext()}
                      className={isNextDisabled && !isSaving ? "bg-background text-stone-300 border-0" : ""}
                    >
                      {isSaving ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          {currentScreen?.getButtonText?.({ answers: flowAnswers, currentStep: flowStep }) ??
                            (flowIsLast ? "Done" : "Next")}
                          <ArrowRight className="h-4 w-4" />
                        </>
                      )}
                    </Button>
                  )}
                </div>
              )}
            </SheetContent>
          </Sheet>

          <CalendlyModal
            isOpen={calendlyOpen}
            onClose={() => setCalendlyOpen(false)}
            url={CALENDLY_URLS.ESTATE_SETTLEMENT_QUOTE}
            onEventScheduled={handleEventScheduled}
          />
        </>
      )}
    </MainLayout>
  );
};
